import { Vue, Component, Prop, Emit } from 'vue-property-decorator'
import { CustomLabelFunction, Includes } from '@/types/common.type'
import {
    ConfigurationRelationshipsStepsState,
    ConfigurationRelationshipStep, ConfigurationRelationshipStepMoved,
    initialConfigurationRelationshipsStepsState
} from '@/modules/configurationModule/shared/components/configuration-relationships-steps/configuration-relationships-steps.model'
import { GridItem, GridLayout } from 'vue-grid-layout'

@Component({
    components: {
        GridLayout,
        GridItem
    }
})
export default class ConfigurationRelationshipsSteps<Option extends object = any, Relationship extends object & Includes<Relationship, Option> = any, Step extends ConfigurationRelationshipStep<Option, Relationship> = any> extends Vue {
    /// SELECT PROPS ///

    /**
     * Array of available options
     */
    @Prop({
        type: Array
    })
    readonly options!: Array<Option>

    /**
     * Function to resolve a promise of array of available options
     */
    @Prop({
        type: Function
    })
    readonly optionLoader!: () => Promise<Array<Option>>

    /**
     * Key to compare options objects
     */
    @Prop({
        type: String,
        default: 'id'
    })
    readonly optionTrackBy!: keyof Option

    /**
     * Label to look for in option Object
     */
    @Prop({
        type: String,
        default: 'label'
    })
    readonly optionLabel!: keyof Option

    /**
     * Function to interpolate the custom option label
     */
    @Prop({
        type: Function
    })
    readonly optionCustomLabel!: CustomLabelFunction<Option>

    /**
     * Loads options from API on search change.
     */
    @Prop({
        type: Boolean,
        default: false
    })
    readonly ajaxSearch!: boolean

    /// ASSOCIATIONS PROPS ///

    /**
     * Array of available steps.
     */
    @Prop({
        type: Array,
        required: true
    })
    readonly steps!: Array<Step>

    /**
     * Key to compare steps objects
     */
    @Prop({
        type: String,
        required: true
    })
    readonly relationshipTrackBy!: keyof Relationship

    /**
     * Label to look for in relationship Object
     */
    @Prop({
        type: String,
        default: 'label'
    })
    readonly relationshipLabel!: keyof Relationship

    /**
     * Function to interpolate the custom relationship label
     */
    @Prop({
        type: Function,
        default: (relationship: Relationship, label: keyof Relationship): Relationship | Relationship[keyof Relationship] | '' => {
            if (relationship) {
                return label ? relationship[label] : relationship
            }
            return ''
        }
    })
    readonly relationshipCustomLabel!: CustomLabelFunction<Relationship>

    /// TITLES PROPS ///

    @Prop({
        type: String,
        required: true
    })
    readonly title!: string

    @Prop({
        type: String,
        required: true
    })
    readonly optionsTitle!: string

    @Prop({
        type: String
    })
    readonly helpText?: string

    @Prop({
        type: String
    })
    readonly noStepsText?: string

    /// EMITTERS ///

    @Emit('create')
    createRelationship(): Option {
        return this.state.option
    }

    @Emit('delete')
    deleteRelationship(relationship: Relationship): Option {
        return relationship
    }

    @Emit('moved')
    movedStep(i: string, newX: number, newY: number): ConfigurationRelationshipStepMoved<Relationship> {
        return {
            i,
            newX,
            newY,
            item: this.steps.find(item => item.i === i)?.item
        }
    }

    /// DATA ///

    state: ConfigurationRelationshipsStepsState<Option, Relationship, Step>

    /// COMPUTED PROPERTIES ///

    get modelValidated(): boolean {
        return !!this.state.option?.[this.optionTrackBy] && !this._isOptionAssociated()
    }

    get showNoRelationshipsText(): boolean {
        return !!this.noStepsText && !this.steps.length
    }

    constructor() {
        super()
        this.state = initialConfigurationRelationshipsStepsState()
    }

    private _getAssociatedOption(relationship: Relationship): Option {
        return relationship[this.relationshipTrackBy] as unknown as Option
    }

    private _isOptionAssociated(): boolean {
        return !!this.steps.find(step => {
            const associatedOption: Option = this._getAssociatedOption(step.item)
            return associatedOption[this.optionTrackBy] === this.state.option[this.optionTrackBy]
        })
    }

    /**
     * Returns empty string when relationship is null/undefined.
     * If has a relationship label, returns the relationshipCustomLabel() results and casts it to string.
     * Otherwise, returns the optionCustomLabel() results and casts it to string.
     *
     * @param relationship
     * @returns {Object||String}
     */
    getRelationshipLabel(relationship: Relationship): Relationship | Relationship[keyof Relationship] | Option | Option[keyof Option] | '' {
        if (relationship) {
            const props = this.$options.propsData
            if (props) {
                const propsKeys = Object.keys(props)
                if (propsKeys.includes('relationshipLabel') || propsKeys.includes('relationshipCustomLabel')) {
                    // uses a custom label from the relationship
                    return this.relationshipCustomLabel(relationship, this.relationshipLabel)
                } else {
                    // uses the option label associated to the relationship
                    const option: Option = this._getAssociatedOption(relationship)
                    if (option) {
                        return this.optionLabel ? option[this.optionLabel] : option
                    }
                }
            }
        }
        return ''
    }
}
